'use client';

import React, { useCallback, useState } from 'react';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { ModalWrapper } from '@/components/modal';
import { TrackedButton } from '@/components/button';
import { useNewsletterSubscribe } from '@/hooks/useNewsletterSubscribe.hook';
import { markNewsletterSubscribed } from '@/utils/newsletter-popup-storage';
import { useNewsletterPopupTrigger } from './useNewsletterPopupTrigger.hook';

// 완료 모달은 motion 을 쓰므로 지연 로드한다 — 구독하지 않는 대다수 방문자의
// 블로그 상세 초기 번들에 모션 런타임이 얹히지 않게 한다
const NewsletterSubscribedModal = dynamic(
  () => import('@/components/newsletter-subscribed-modal'),
  { ssr: false },
);

const TITLE_ID = 'newsletter-popup-title';
const MESSAGE_ID = 'newsletter-popup-message';

type NewsletterPopupProps = {
  /**
   * 다른 팝업이 이미 이 글을 차지하는 경우 (`post.popupEnable` = 자료 다운로드
   * 팝업). 같은 50% 지점에서 둘이 겹치지 않게 이쪽이 물러난다.
   */
  disabled?: boolean;
};

/**
 * 모바일 뉴스레터 구독 팝업 (Figma 2576-29432 국문 / 2635-66757 영문).
 *
 * 노출 시점은 `useNewsletterPopupTrigger` 가 정한다(모바일 · 1·4·7번째 글 ·
 * 스크롤 50%). 구독에 성공하면 팝업을 닫고 완료 모달로 넘긴다.
 */
export default function NewsletterPopup({
  disabled = false,
}: NewsletterPopupProps) {
  const t = useTranslations('Blog.newsletter');
  const { isOpen, close } = useNewsletterPopupTrigger(disabled);
  const [isSubscribedOpen, setIsSubscribedOpen] = useState(false);

  const handleSubscribed = useCallback(() => {
    markNewsletterSubscribed();
    setIsSubscribedOpen(true);
    close();
  }, [close]);

  // 완료 모달의 자동 닫힘 타이머가 리렌더마다 초기화되지 않게 참조를 고정한다
  const closeSubscribed = useCallback(() => setIsSubscribedOpen(false), []);

  const { email, messageType, isLoading, handleEmailChange, handleSubmit } =
    useNewsletterSubscribe({
      leadLabel: 'newsletter_popup',
      source: 'blog_newsletter_popup',
      onSubscribed: handleSubscribed,
    });

  const placeholder = t('placeholder');
  // onSubscribed 를 넘겼으므로 성공 messageType 은 세워지지 않는다 — 여기 남는
  // 것은 실패·검증 메시지뿐이다
  const message = messageType ? t(`message.${messageType}`) : null;

  return (
    <>
      <ModalWrapper
        isOpen={isOpen}
        onClose={close}
        ariaLabelledBy={TITLE_ID}
        backdropOpacity={0.2}
        position="bottom"
      >
        <form
          onSubmit={handleSubmit}
          className="relative mx-5 mb-5 flex w-[350px] max-w-full flex-col gap-5 rounded-[20px] bg-3b-background-surface p-5"
        >
          <div className="flex flex-col gap-3">
            <h2
              id={TITLE_ID}
              className="text-center text-sh1 font-semibold text-black"
            >
              {t.rich('title', { br: () => <br /> })}
            </h2>
            <input
              type="email"
              value={email}
              onChange={handleEmailChange}
              placeholder={placeholder}
              aria-label={placeholder}
              aria-invalid={messageType === 'invalid-email' || undefined}
              aria-describedby={message ? MESSAGE_ID : undefined}
              autoComplete="email"
              className="w-full rounded-[8px] border border-3b-background-200 bg-white px-3 py-[11px] text-[16px] leading-[26px] text-3b-text-primary placeholder:text-3b-text-hint"
            />
          </div>
          <div className="flex gap-3">
            <TrackedButton
              label="newsletter_popup_close"
              position="modal"
              type="button"
              onClick={close}
              // hover 배경을 더 어둡게 하면 text-secondary 와의 대비가
              // 4.43:1 로 떨어져 WCAG AA(4.5:1) 미달이 된다. 기본 배경에서는
              // 4.94:1 로 통과하므로 hover 색 변화를 두지 않는다
              className="flex-1 rounded-[10px] bg-3b-background-medium px-2.5 py-4 text-[14px] font-semibold leading-4 tracking-[-0.128px] text-3b-text-secondary"
            >
              {t('popup.close')}
            </TrackedButton>
            {/* type="submit" 이어야 form 의 onSubmit(handleSubmit)이 돈다 —
                "button" 이면 폼 안에 있어도 암묵적 제출이 일어나지 않아 클릭이
                gtag 만 남기고 아무 일도 하지 않는다 */}
            <TrackedButton
              label="newsletter_popup_subscribe"
              position="modal"
              type="submit"
              disabled={isLoading}
              className="flex-1 rounded-[10px] bg-3b-blue-light-900 px-2.5 py-4 text-[14px] font-semibold leading-4 tracking-[-0.128px] text-3b-text-contrast hover:bg-3b-blue-light-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? t('loading') : t('button')}
            </TrackedButton>
          </div>
          {message ? (
            <p
              id={MESSAGE_ID}
              role="alert"
              className="text-center text-caption text-red-600"
            >
              {message}
            </p>
          ) : null}
        </form>
      </ModalWrapper>
      {/* 팝업 뒤에 둔다 — ModalWrapper 가 body 스크롤을 잠그므로, 팝업이 닫히며
          풀어놓은 잠금을 완료 모달이 다시 걸어야 순서가 맞는다.
          열릴 때만 렌더해야 dynamic 이 실제로 지연된다 — 닫힌 채로 렌더하면
          마운트 시점에 청크를 바로 받아 지연 로드가 무의미해진다 */}
      {isSubscribedOpen ? (
        <NewsletterSubscribedModal isOpen onClose={closeSubscribed} />
      ) : null}
    </>
  );
}
